<template>
    <div>
        <div>
            <div class="header">
                <h2>Choose your new plan</h2>
            </div>
            <div class="switch-period">
                <ul>
                    <li :class="{'active': !yearly}" @click="yearly = false"><a href="javascript:;" title="View the monthly plans">Monthly</a></li>
                    <li :class="{'active': yearly}" @click="yearly = true"><a href="javascript:;" title="View the yearly plans">Yearly</a></li>
                </ul>
                <p :class="{'visible': yearly}">Two months off! Sweet!</p>
            </div>
            <div class="progress-bar">
                <h5>Select your monthly conversion needs</h5>
                <div style="width: 100%; margin: 3rem 0">
                    <div class="progress-bar__full" @click="updateThumbPosition" ref="sliderTrack">
                        <div class="progress-bar__fill" :style="{ width: thumbPosition + '%' }">
                            <div class="progress-bar__drag"></div>
                        </div>
                    </div>
                </div>
                <div class="form white">
                    <label for="input-number-credits">I'll need to convert</label>
                    <input id="input-number-credits" class="input" type="number" min="50" v-model="inputValue" @input="onInputValueChange" />
                    <span>documents* per month</span>
                </div>
                <div style="font-size: .8em; margin-top: 10px; text-align: center;">
                    <p>(* We count one document per 5Mb of generated data. A 14Mb document will count for 3 credits)</p>
                </div>
            </div>
            <div class="plans">
                <div class="plan" v-for="plan in visiblePlans" :key="plan.name" @click="selectPlan(plan)" :class="{'hoverable': !isCurrentPlan(plan)}">
                    <h4>{{ plan.display.replace(' Yearly', '') }}</h4>
                    <div class="price">
                        <span>${{ plan.price }}</span>
                        <small>/<template v-if="plan.yearly">year</template><template v-else>month</template></small>
                    </div>
                    <ul>
                        <li>
                            <svg-check />
                            {{ plan.credits|number }} credits per month
                        </li>
                        <li>
                            <svg-check />
                            Overage: <strong>{{ (plan.overage / 1000).toFixed(3).replace(/0$/, '') }}$</strong> per conv.
                        </li>
                        <li><svg-check /> PDF compression</li>
                        <li><svg-check /> Parallel conversion</li>
                    </ul>
                    <div class="action">
                        <template v-if="isCurrentPlan(plan)">
                            <p><svg-check /> Your current plan</p>
                        </template>
                        <template v-else>
                            <button class="btn" @click.stop="selectPlan(plan)">Choose this plan</button>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import SvgCheck from '@/assets/images/check-circle.svg'

export default {
    components: { SvgCheck },
    data () {
        return {
            thumbPosition: 0, // Adjust !
            inputValue: 50,
            yearly: false,
            isDradding: false,
            selectedPlan: null
        }
    },
    computed: {
        ...mapState({
            account: state => state
        }),
        plans () {
            return this.$parent.plans.filter(plan => plan.yearly === this.yearly).sort((a, b) => a.credits - b.credits)
        },
        visiblePlans () {
            // Returns the previous one, the current one, and the next one
            // If there is no previous, return the next two ones:
            // If there is no next, return the previous two ones
            if (this.selectedPlan === null) {
                return this.plans.slice(0, 3)
            }

            const index = this.plans.findIndex(plan => plan.name === this.selectedPlan.name)
            if (index === 0) {
                return this.plans.slice(0, 3)
            } else if (index === this.plans.length - 1) {
                return this.plans.slice(-3)
            } else {
                return this.plans.slice(index - 1, index + 2)
            }
        },
        totalRange () {
            let highest = 0
            for (let i = 0; i < this.plans.length; i++) {
                if (this.plans[i].credits > highest) {
                    highest = this.plans[i].credits
                }
            }
            return highest
        },
        planRanges () {
            return this.plans.map(plan => (plan.credits / this.totalRange) * this.totalRange)
        },
        sectionWidth () {
            return this.plans.length - 1
        },
        displayValue () {
            if (this.thumbPosition < 99) {
                for (let i = 0; i < this.plans.length; i++) {
                    const sectionStart = this.sectionWidth * i
                    const sectionEnd = sectionStart + this.sectionWidth

                    if (this.thumbPosition >= sectionStart && this.thumbPosition < sectionEnd) {
                        // Determine the value within the current section
                        const sectionPosition = (this.thumbPosition - sectionStart) / this.sectionWidth
                        const rangeStart = i === 0 ? 50 : this.plans[i - 1].credits
                        const rangeEnd = this.plans[i].credits
                        return Math.max(Math.round(rangeStart + sectionPosition * (rangeEnd - rangeStart)), 1)
                    }
                }
            }

            return this.totalRange
        }
    },
    watch: {
        thumbPosition (to) {
            this.inputValue = this.displayValue
            for (let i = 0; i < this.plans.length; i++) {
                if (this.plans[i].credits >= this.displayValue) {
                    this.selectedPlan = this.plans[i]
                    break
                }
            }
        },
        yearly (to) {
            for (let i = 0; i < this.plans.length; i++) {
                if (this.plans[i].credits >= this.displayValue) {
                    this.selectedPlan = this.plans[i]
                    break
                }
            }
        }
    },
    mounted () {
        this.$refs.sliderTrack.addEventListener('touchstart', this.startDrag, { passive: true })
        this.$refs.sliderTrack.addEventListener('mousedown', this.startDrag)

        document.addEventListener('touchend', this.stopDrag)
        document.addEventListener('touchcancel', this.stopDrag, false)
        document.addEventListener('touchleave', this.stopDrag, false)
        document.addEventListener('touchmove', this.updateThumbPosition)
        document.addEventListener('mouseup', this.stopDrag)
        document.addEventListener('mousemove', this.updateThumbPosition)

        if (this.account.plan) {
            this.yearly = this.account.plan.yearly
            this.thumbPosition = this.findPositionForValue(this.account.plan.credits + 1)
        }
    },
    beforeUnmount () {
        if (this.$refs.sliderTrack) {
            this.$refs.sliderTrack.removeEventListener('touchstart', this.startDrag)
            this.$refs.sliderTrack.removeEventListener('mousedown', this.startDrag)
        }

        document.removeEventListener('touchend', this.stopDrag)
        document.removeEventListener('touchcancel', this.stopDrag)
        document.removeEventListener('touchleave', this.stopDrag)
        document.removeEventListener('touchmove', this.updateThumbPosition)
        document.removeEventListener('mouseup', this.stopDrag)
        document.removeEventListener('mousemove', this.updateThumbPosition)
    },
    methods: {
        selectPlan (plan) {
            if (this.isCurrentPlan(plan)) {
                return false
            }

            this.$router.push({ name: 'upgrade-settings', query: { plan: plan.name } })
        },
        isCurrentPlan (plan) {
            return this.account.plan && this.account.plan.name === plan.name
        },
        startDrag () {
            this.isDragging = true
        },
        stopDrag ($event) {
            this.updateThumbPosition($event)
            this.isDragging = false
        },
        findPositionForValue (value) {
            for (let i = 0; i < this.plans.length; i++) {
                const rangeStart = i === 0 ? 50 : this.plans[i - 1].credits
                const rangeEnd = this.plans[i].credits
                if (value >= rangeStart && value <= rangeEnd) {
                    const sectionStart = this.sectionWidth * i
                    return sectionStart + ((value - rangeStart) / (rangeEnd - rangeStart)) * this.sectionWidth
                }
            }
            return 100 // Default to end if value exceeds max range
        },
        onInputValueChange () {
            const value = Math.min(Math.max(this.inputValue, 50), this.totalRange) // Clamping the value
            this.thumbPosition = this.findPositionForValue(value)
        },
        updateThumbPosition ($event) {
            if ($event.type !== 'click' && !this.isDragging) return // Only update if dragging or clicking
            if ($event.type === 'touchend') return // No clientX on end, but we had the value from previous

            const bounds = this.$refs.sliderTrack.getBoundingClientRect()
            let clientX = null

            if ($event.type === 'touchmove') {
                clientX = $event.touches[0].clientX
            } else {
                $event.preventDefault()
                clientX = $event.clientX
            }

            let position = (clientX - bounds.left) / bounds.width * 100

            // Boundary checks
            position = Math.max(position, 0) // Prevent going beyond left boundary
            position = Math.min(position, 99) // Prevent going beyond right boundary
            this.thumbPosition = position
        }
    }
}
</script>

<style lang="scss" scoped>
    @import '../../../public/css/vars';

.switch-period {
    text-align: center;
    margin-bottom: 40px;

    li {
        display: inline-block;
        cursor: pointer;
        a {
            display: inline-block;
            padding: 10px 40px;
            background-color: #fff;
            border: solid 1px $text-color-light;
            transition: background-color $transition, color $transition;
            color: $text-color-light;

            &:hover {
                background-color: #eee;
                color: darken($text-color-light, 20%)
            }
        }

        &.active>a {
            background-color: $menu-active-color;
            border-color: $menu-active-color;
            color: #fff;
        }

        &:first-child>a {
            border-radius: 12px 0 0 12px;
            border-right: none;
        }
        &:last-child>a {
            border-radius: 0 12px 12px 0;
            border-left: none;
        }
    }

    &>p {
        animation: .5s ease-in-out 0s 1 forwards fadeOutTop;
        transform: translate(0px, 0px);
        margin-top: 10px;
        font-style: italic;
        color: $menu-active-color;
        display: none;

        &.visible {
            display: block;
            animation: .5s ease-in-out 0s 1 forwards fadeInTop;
        }
    }
}

.progress-bar {
    background-color: #fff;
    border-radius: 12px;
    padding: 20px;

    .form {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        justify-content: center;

        input {
            width: auto;
            min-width: 100px;
            margin-left: 20px;
            margin-right: 20px;
        }

        @media (min-width: 768px) {
            flex-direction: row;
        }
    }

    &__full {
        position: relative;
        border-radius: 0.375rem;
        height: 14px;
        background: linear-gradient(89.47deg, #FF6F6F -5.09%, #F8B654 115.63%);
        cursor: pointer;
    }

    &__fill {
        position: absolute;
        top: 0;
        left: 0;
        height: 14px;
        border-radius: 12px;
        background-color: $menu-active-color;
    }

    &__drag {
        display: block;
        position: absolute;
        top: -4px;
        right: -12px;
        border-radius: 9999px;
        height: 22px;
        width: 22px;
        cursor: pointer;
        background-color: $menu-active-color;
    }
}

.plans {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    align-items: center;
    margin-top: 40px;
}

.plan {
    flex: 1 0 33%; // flex 1 or not ?
    max-width: calc(34% - 40px);
    position: relative;
    background-color: #fff;
    border-radius: 12px;
    box-shadow: $box-shadow;
    margin: 0 10px;
    padding: 20px;
    text-align: center;

    &.hoverable {
        cursor: pointer;
        &:hover .btn {
            box-shadow: 2px 2px 4px fade-out(lighten($button-background-color, 20%), 0.2);
            color: #fff
        }
    }

    &:first-child {
        margin-left: 0;
    }

    &:nth-child(3n+1) { margin-left: 0; }
    &:nth-child(3n+3) { margin-right: 0; }
    &:nth-child(n+4) { margin-top: 20px; }

    &:before {
        position: absolute;
        content: '';
        top: 0;
        left: 0;
        right: 0;
        height: 8px;
        border-radius: 12px 12px 0 0;
    }

    &:nth-child(1):before { background: linear-gradient(89.47deg, #6C20CD -5.09%, #EA8A8A 115.63%) }
    &:nth-child(2):before { background: linear-gradient(89.47deg, #FF6F6F -5.09%, #F8B654 115.63%) }
    &:nth-child(3):before { background: linear-gradient(89.47deg, #38E4BB -5.09%, #5495F8 115.63%) }
    &:nth-child(4):before { background: linear-gradient(89.47deg, #FF6F6F -5.09%, #F854D3 115.63%) }
    &:nth-child(5):before { background: linear-gradient(89.47deg, #20AECD -5.09%, #DC8AEA 115.63%) }

    h4 {
        font-size: 2rem;
        margin: 10px 0 20px;
        color: $link-color-secondary
    }

    .price span {
        color: $menu-active-color;
        font-size: 4.8rem;
        font-weight: bold;
    }

    .price small {
        color: $link-color-secondary
    }

    svg {
        height: 24px;
        width: 24px;
        stroke: $text-color;
        vertical-align: middle;
        margin-right: 5px;
    }

    ul {
        margin: 10px 0 20px;
        border-top: solid 1px $body-background-color;
        li {
            padding: 10px 0;
            text-align: left;
        }
    }

    .action {
        color: $menu-active-color;
        p {
            margin: 0;
            padding: 12px 40px;
            font-size: 1.6rem;
            line-height: 1.6rem;
        }
        svg { stroke: $menu-active-color }
    }

    .btn {
        padding: 15px 40px;
    }
}

</style>
